import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/cn";
import HistoryIcon from "@mui/icons-material/History";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { SvgIconComponent } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { NavLinkRow } from "@/components/layout/SidebarNavRows";
import { Tooltip } from "@/components/ui/Tooltip";
import {
  resolveGroupIcon,
  SidebarNavFlyout,
} from "@/components/layout/SidebarNavFlyout";
import { SidebarEdgeToggle } from "@/components/layout/SidebarEdgeToggle";
import { WORKSPACE_NAV_GROUP_ICONS } from "@/config/workspaceNavGroupIcons";
import { resolveBackendLabel, resolveGroupLabel } from "@/i18n/navLabel";

export interface NavItem {
  to: string;
  label: string;
  labelKey?: string;
  icon: SvgIconComponent;
  end?: boolean;
  /** Required feature code (org tenant) — shown when module is on; locked if missing */
  feature?: string;
}

/** @deprecated Use NavWorkflowGroup for per-app sidebars */
export interface NavSection {
  id: string;
  label: string;
  module?: string;
  items: NavItem[];
}

/** Business workflow group within a single application sidebar */
export interface NavWorkflowGroup {
  id: string;
  label: string;
  labelKey?: string;
  items: NavItem[];
  /** Dashboard group — always visible, no collapse */
  pinned?: boolean;
}

interface SidebarProps {
  items?: NavItem[];
  /** @deprecated Use workflowGroups */
  sections?: NavSection[];
  workflowGroups?: NavWorkflowGroup[];
  /** Quick access — recent routes in this workspace */
  recentItems?: NavItem[];
  favoriteItems?: NavItem[];
  collapsed: boolean;
  onToggle?: () => void;
  /** Primary sidebar heading — usually the active module/workspace name */
  brand?: string;
  /** Secondary sidebar line — short scope summary related to the heading */
  appLabel?: string;
  /** Active nav highlight — brand primary only in shell */
  accent?: "violet" | "slate";
  hasFeature?: (code: string) => boolean;
  /** Fixed footer — e.g. app switcher */
  footer?: ReactNode;
}

const QUICK_ACCESS_ICONS: Record<string, SvgIconComponent> = {
  favorites: StarBorderIcon,
  recent: HistoryIcon,
};

function pathMatchesItem(pathname: string, to: string): boolean {
  const base = to.split("?")[0];
  return (
    pathname === base ||
    (base !== "/pos/dashboard" && pathname.startsWith(`${base}/`))
  );
}

function groupIsActive(pathname: string, items: NavItem[]): boolean {
  return items.some((item) => pathMatchesItem(pathname, item.to));
}

function WorkflowGroupBlock({
  group,
  collapsed,
  expanded,
  onToggle,
  hasFeature,
}: {
  group: NavWorkflowGroup;
  collapsed: boolean;
  expanded: boolean;
  onToggle: () => void;
  hasFeature?: (code: string) => boolean;
  accent?: NonNullable<SidebarProps["accent"]>;
}) {
  const { t } = useTranslation();
  const groupLabel = group.labelKey
    ? resolveBackendLabel(group.labelKey, group.label, t)
    : resolveGroupLabel(group.id, group.label, t);

  if (group.items.length === 0) return null;

  const GroupIcon = resolveGroupIcon(group, WORKSPACE_NAV_GROUP_ICONS);

  if (collapsed) {
    if (group.items.length === 1) {
      const item = group.items[0];
      const groupIcon = resolveGroupIcon(group, WORKSPACE_NAV_GROUP_ICONS);
      return (
        <div className="workspace-nav-group">
          <NavLinkRow
            item={item}
            collapsed
            iconOverride={
              WORKSPACE_NAV_GROUP_ICONS[group.id] ? groupIcon : undefined
            }
            locked={Boolean(
              item.feature && hasFeature && !hasFeature(item.feature!),
            )}
          />
        </div>
      );
    }
    return (
      <div className="workspace-nav-group">
        <SidebarNavFlyout
          label={groupLabel}
          icon={resolveGroupIcon(group, WORKSPACE_NAV_GROUP_ICONS)}
          items={group.items}
          hasFeature={hasFeature}
        />
      </div>
    );
  }

  if (group.items.length === 1) {
    const item = group.items[0];
    return (
      <div className="workspace-nav-group">
        <NavLinkRow
          item={item}
          collapsed={false}
          iconOverride={WORKSPACE_NAV_GROUP_ICONS[group.id] ? GroupIcon : undefined}
          locked={Boolean(
            item.feature && hasFeature && !hasFeature(item.feature!),
          )}
        />
      </div>
    );
  }

  return (
    <div className="workspace-nav-group">
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          "workspace-nav-group-parent",
          expanded && "workspace-nav-group-parent--open",
        )}
        aria-expanded={expanded}
      >
        <GroupIcon sx={{ fontSize: 20 }} className="shrink-0 opacity-90" />
        <span className="min-w-0 flex-1 truncate text-left">{groupLabel}</span>
        {expanded ? (
          <ExpandMoreIcon sx={{ fontSize: 18 }} className="shrink-0 opacity-60" />
        ) : (
          <ChevronRightIcon sx={{ fontSize: 18 }} className="shrink-0 opacity-60" />
        )}
      </button>
      {expanded && (
        <div className="workspace-nav-subitems">
          {group.items.map((item) => (
            <NavLinkRow
              key={item.to}
              item={item}
              collapsed={false}
              variant="sub"
              showIcon={false}
              locked={Boolean(
                item.feature && hasFeature && !hasFeature(item.feature!),
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function QuickAccessBlock({
  id,
  label,
  items,
  collapsed,
  headerSlot = false,
}: {
  id: string;
  label: string;
  items: NavItem[];
  collapsed: boolean;
  /** Collapsed rail — pin Recent in the sidebar header instead of the module logo */
  headerSlot?: boolean;
}) {
  if (!headerSlot && items.length === 0) return null;
  if (headerSlot && items.length === 0) {
    return (
      <Tooltip content={label} placement="right">
        <span
          className="workspace-sidebar-recent-slot flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 dark:text-slate-500"
          aria-hidden
        >
          <HistoryIcon sx={{ fontSize: 20 }} />
        </span>
      </Tooltip>
    );
  }

  if (collapsed) {
    const slotClass = headerSlot ? "workspace-sidebar-recent-slot" : "workspace-nav-group";
    if (items.length === 1) {
      return (
        <div className={slotClass}>
          <NavLinkRow
            item={items[0]}
            collapsed
            iconOverride={id === "recent" ? HistoryIcon : undefined}
          />
        </div>
      );
    }
    return (
      <div className={slotClass}>
        <SidebarNavFlyout
          label={label}
          icon={QUICK_ACCESS_ICONS[id] ?? items[0].icon}
          items={items}
        />
      </div>
    );
  }

  return (
    <div className="workspace-nav-section">
      <p className="workspace-nav-section-label">{label}</p>
      <div className="workspace-nav-subitems workspace-nav-subitems--section">
        {items.map((item) => (
          <NavLinkRow
            key={`${label}-${item.to}`}
            item={item}
            collapsed={false}
            variant="sub"
            showIcon={false}
          />
        ))}
      </div>
    </div>
  );
}

export function Sidebar({
  items = [],
  sections = [],
  workflowGroups = [],
  recentItems = [],
  favoriteItems = [],
  collapsed,
  onToggle,
  appLabel = "",
  brand = "",
  accent = "violet",
  hasFeature,
  footer,
}: SidebarProps) {
  const { t } = useTranslation(['navigation', 'common']);
  const location = useLocation();
  const useWorkflow = workflowGroups.length > 0;
  const useSections = !useWorkflow && sections.length > 0;

  const activeGroupId = useMemo(() => {
    if (!useWorkflow) return null;
    const match = workflowGroups.find((g) =>
      groupIsActive(location.pathname, g.items),
    );
    return match?.id ?? "dashboard";
  }, [location.pathname, useWorkflow, workflowGroups]);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!activeGroupId) return;
    setExpanded((prev) => ({ ...prev, [activeGroupId]: true }));
  }, [activeGroupId]);

  const toggleGroup = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <aside
      className={cn(
        "workspace-sidebar",
        collapsed && "workspace-sidebar--collapsed",
      )}
    >
      {onToggle && (
        <SidebarEdgeToggle
          collapsed={collapsed}
          onToggle={onToggle}
          variant="rail"
        />
      )}
      <div className="workspace-sidebar-header">
        {collapsed ? (
          <QuickAccessBlock
            id="recent"
            label={t('sidebar.recent')}
            items={recentItems}
            collapsed
            headerSlot
          />
        ) : (
          <div className="min-w-0 flex-1">
            {brand && (
              <p className="workspace-sidebar-brand truncate">{brand}</p>
            )}
            {appLabel && (
              <p className="workspace-sidebar-app truncate">{appLabel}</p>
            )}
          </div>
        )}
      </div>

      <div className="workspace-sidebar-body">
        <nav className="workspace-sidebar-nav" aria-label={t('labels.workspaceNavigation')}>
          {!collapsed ? (
            <QuickAccessBlock
              id="recent"
              label={t('sidebar.recent')}
              items={recentItems}
              collapsed={false}
            />
          ) : null}
          <QuickAccessBlock
            id="favorites"
            label={t('sidebar.favorites')}
            items={favoriteItems}
            collapsed={collapsed}
          />
          {useWorkflow
            ? workflowGroups.map((group) => (
                <WorkflowGroupBlock
                  key={group.id}
                  group={group}
                  collapsed={collapsed}
                  expanded={
                    collapsed
                      ? false
                      : (expanded[group.id] ?? groupIsActive(location.pathname, group.items))
                  }
                  onToggle={() => toggleGroup(group.id)}
                  hasFeature={hasFeature}
                  accent={accent}
                />
              ))
            : useSections
              ? sections.map((section) => (
                  <WorkflowGroupBlock
                    key={section.id}
                    group={{
                      id: section.id,
                      label: section.label,
                      items: section.items,
                    }}
                    collapsed={collapsed}
                    expanded={
                      collapsed
                        ? false
                        : (expanded[section.id] ??
                          groupIsActive(location.pathname, section.items))
                    }
                    onToggle={() => toggleGroup(section.id)}
                    hasFeature={hasFeature}
                    accent={accent}
                  />
                ))
              : items.map((item) => (
                  <NavLinkRow
                    key={item.to}
                    item={item}
                    collapsed={collapsed}
                  />
                ))}
        </nav>
        {footer ? (
          <div className="workspace-sidebar-footer">{footer}</div>
        ) : null}
      </div>
    </aside>
  );
}
